'use client';

import type { Translate } from 'next-translate';
import type { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { PlayCircleOutline } from '@eqi-investimentos/icons/web';
import { Button, Flex, Spinner, Text } from '@eqi-investimentos/react';
import { Divider } from '@chakra-ui/react';
import { Content } from '@/modules/Entertainment/interface/Content';
import { Program } from '@/modules/Entertainment/interface/Program';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { useBookmark } from '@/modules/Entertainment/http/client/mutations/useBookmark';
import { Icon } from '@/components/atoms/Icon';
import { getHrefRecentContent } from '@/utils/content';
import { IfRender } from '@/utils/jsx';
import { HasRecommendationTag } from './HasRecommendationTag';
export const RecentContentCard: FC<RecentContentCardProps> = ({
  id,
  authorName,
  programTitle,
  publishedAt,
  summary,
  title,
  thumb,
  hasRecommendation,
  isSaved,
  slug,
  object,
  program_type,
  content_count,
  t
}) => {
  const {
    mutate,
    isPending
  } = useBookmark(['last', 'content']);
  const {
    mutate: sendClickEvent
  } = useSendClickEvent();
  let labelQtdContent = '';
  if (content_count) {
    labelQtdContent = content_count > 1 ? t('attributes.contents') : t('attributes.content');
  }
  const hrefCard = getHrefRecentContent({
    object: object,
    program_type,
    slug
  });
  const handleSendClick = () => {
    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.researchContent.label(t('attributes.open'), hrefCard)
    });
  };
  return <Flex pos="relative" flexDir="column" justifyContent="space-between" gap="5" w="21.75rem" h="31.4375rem" p="l" border="1px solid" borderColor="border.secondary" borderRadius="lg" transition="0.4s all ease-in-out" _hover={{
    bg: 'background.brandLight',
    '#badge-recomendation-header': {
      bg: 'content.brand',
      p: {
        color: 'content.inverse'
      }
    }
  }} data-sentry-element="Flex" data-sentry-component="RecentContentCard" data-sentry-source-file="RecentContentCard.tsx">
      <Flex as={Link} href={hrefCard} passHref onClick={() => handleSendClick()} w="full" h="10.75rem" data-sentry-element="Flex" data-sentry-source-file="RecentContentCard.tsx">
        <Image src={thumb ?? ''} width={308} height={172} alt={programTitle} style={{
        borderRadius: '0.25rem',
        cursor: 'pointer',
        objectFit: 'cover'
      }} onError={({
        currentTarget
      }) => {
        currentTarget.onerror = null;
        currentTarget.src = '/assets/placeholder-content.webp';
      }} data-sentry-element="Image" data-sentry-source-file="RecentContentCard.tsx" />
      </Flex>
      <Flex flexDir="column" minH="12.5rem" alignItems="flex-start" gap="s" data-sentry-element="Flex" data-sentry-source-file="RecentContentCard.tsx">
        <Flex w="full" h="1.5625rem" gap="0.625rem" justifyContent="space-between" alignItems="center" data-sentry-element="Flex" data-sentry-source-file="RecentContentCard.tsx">
          <IfRender condition={object == 'program'} data-sentry-element="IfRender" data-sentry-source-file="RecentContentCard.tsx">
            <Text fontSize="sm" color="content.tertiary" data-sentry-element="Text" data-sentry-source-file="RecentContentCard.tsx">
              {content_count} {labelQtdContent}
            </Text>
          </IfRender>
          <IfRender condition={object != 'program'} data-sentry-element="IfRender" data-sentry-source-file="RecentContentCard.tsx">
            <Text fontSize="sm" color="content.tertiary" data-sentry-element="Text" data-sentry-source-file="RecentContentCard.tsx">
              {publishedAt}
            </Text>
          </IfRender>

          {hasRecommendation && <HasRecommendationTag t={t} />}
        </Flex>
        <Text fontSize="lg" fontWeight="bold" lineHeight="2xl" color="content.primary" noOfLines={1} data-sentry-element="Text" data-sentry-source-file="RecentContentCard.tsx">
          {title}
        </Text>

        <Text color="content.tertiary" fontWeight="light" fontSize="sm" data-sentry-element="Text" data-sentry-source-file="RecentContentCard.tsx">
          {getTextProgramTitleAndAuthorName(programTitle, authorName, object)}
        </Text>

        <Text fontSize="md" fontWeight="normal" color="content.primary" lineHeight="xl" noOfLines={3} dangerouslySetInnerHTML={{
        __html: summary
      }} data-sentry-element="Text" data-sentry-source-file="RecentContentCard.tsx" />
      </Flex>
      <Flex flexDir="column" gap="12" justify="space-between" data-sentry-element="Flex" data-sentry-source-file="RecentContentCard.tsx">
        <Divider borderTop="0.5px solid" borderColor="border.secondary" opacity={0.3} data-sentry-element="Divider" data-sentry-source-file="RecentContentCard.tsx" />
        <Flex minH="35px" gap="8" align="center" justifyContent="space-between" data-sentry-element="Flex" data-sentry-source-file="RecentContentCard.tsx">
          <Flex as={Link} href={hrefCard} onClick={() => handleSendClick()} color="content.primary" gap="0.625rem" pl="1" pr="0" h="1.5625rem" alignItems="center" _hover={{
          color: 'content.brand'
        }} data-sentry-element="Flex" data-sentry-source-file="RecentContentCard.tsx">
            <PlayCircleOutline aria-label={t('attributes.open')} w="24" h="24" color="content.brand" transitionDuration="0ms" data-sentry-element="PlayCircleOutline" data-sentry-source-file="RecentContentCard.tsx" />
            <Text fontSize="sm" fontWeight="normal" lineHeight="xl" data-sentry-element="Text" data-sentry-source-file="RecentContentCard.tsx">
              {t('attributes.open')}
            </Text>
          </Flex>
          <Button variant="naked" gap="0.625rem" px="0" h="1.5625rem" w="4.625rem" color={isSaved ? 'content.brand' : 'content.primary'} _hover={{
          bg: 'transparent'
        }} leftIcon={<Icon aria-label={isSaved ? t('attributes.saved') : t('attributes.save')} iconName={isSaved ? 'BookmarkRemoveOutline' : 'BookmarkAddOutline'} w="24" h="24" color="content.brand" transitionDuration="0ms" />} onClick={() => mutate({
          id,
          bookmarked: isSaved,
          object: object
        })} sx={{
          svg: {
            fill: isSaved ? 'content.brand' : 'inherit'
          },
          '.chakra-spinner': {
            mt: '6px',
            w: '1.125rem',
            h: '1.125rem'
          }
        }} data-sentry-element="Button" data-sentry-source-file="RecentContentCard.tsx">
            {isPending && <Spinner />}
            {!isPending && (isSaved ? t('attributes.saved') : t('attributes.save'))}
          </Button>
        </Flex>
      </Flex>
    </Flex>;
};
type RecentContentCardProps = {
  id: Content['id'];
  slug: Content['slug'];
  authorName: Content['author_name'];
  programTitle: Content['program_title'];
  publishedAt: Content['published_at'];
  summary: Content['summary'];
  title: Content['title'];
  thumb: Content['thumb'];
  hasRecommendation: boolean;
  isSaved: boolean;
  t: Translate;
  object: Content['object'];
  program_type: Content['program_type'];
  content_count: Program['content_count'];
};
const getTextProgramTitleAndAuthorName = (programTitle: string, authorName: string, object: string) => {
  if (object === 'program') {
    if (authorName) {
      return authorName;
    }
    return '';
  }
  if (programTitle && authorName) {
    return `${programTitle} • ${authorName}`;
  }
  return programTitle || authorName || '';
};